import React, { useState, useEffect } from "react";
import { Dialog, IconButton } from "@mui/material";
import {
  FileText,
  User,
  Calendar,
  BookOpen,
  MessageSquare,
  X,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Users,
  Video,
  Info,
  Paperclip,
  Download,
  ExternalLink,
} from "lucide-react";
import { ExplanationRequest } from "../../../types/explanations";
import {
  downloadFile,
  getFileDetails,
} from "../../../server/Files CRUD/Requests";
import { GetFileDetailsOutput } from "../../../server/Files CRUD/OutputsTypes";
import { getSchoolClassLabel } from "../../common/utils";

interface ExplanationDetailsModalProps {
  open: boolean;
  request: ExplanationRequest | any;
  onClose: () => void;
}

const getStatusInfo = (status: string) => {
  const statusMap: Record<
    string,
    { label: string; colorClass: string; icon: React.ReactNode }
  > = {
    pending: {
      label: "En attente",
      colorClass: "bg-yellow-100 text-yellow-800",
      icon: <Clock className="w-4 h-4" />,
    },
    validated: {
      label: "Validée - Prof assigné",
      colorClass: "bg-blue-100 text-blue-800",
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    slots_proposed: {
      label: "Créneaux proposés",
      colorClass: "bg-indigo-100 text-indigo-800",
      icon: <Calendar className="w-4 h-4" />,
    },
    student_confirmed: {
      label: "Créneau confirmé",
      colorClass: "bg-green-100 text-green-800",
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    meeting_scheduled: {
      label: "Réunion programmée",
      colorClass: "bg-green-100 text-green-800",
      icon: <Video className="w-4 h-4" />,
    },
    scheduled: {
      label: "Séance programmée",
      colorClass: "bg-green-100 text-green-800",
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    completed: {
      label: "Terminée",
      colorClass: "bg-gray-100 text-gray-800",
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    rejected: {
      label: "Rejetée",
      colorClass: "bg-red-100 text-red-800",
      icon: <XCircle className="w-4 h-4" />,
    },
  };
  return (
    statusMap[status] || {
      label: "Statut inconnu",
      colorClass: "bg-gray-100 text-gray-800",
      icon: <AlertCircle className="w-4 h-4" />,
    }
  );
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return "Non disponible";
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ExplanationDetailsModal: React.FC<ExplanationDetailsModalProps> = ({
  open,
  request,
  onClose,
}) => {
  const [detailedRequest, setDetailedRequest] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<GetFileDetailsOutput[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [meeting, setMeeting] = useState<any>(null);

  useEffect(() => {
    if (open && request?._id) {
      const fetchDetailedRequest = async () => {
        try {
          setLoading(true);
          setDetailedRequest(request);

          // Récupérer les fichiers si fileIds existe
          if (
            request &&
            "fileIds" in request &&
            request.fileIds &&
            request.fileIds.length > 0
          ) {
            console.log("FileIds trouvés:", request.fileIds);
            setFilesLoading(true);

            try {
              // Récupérer les détails de chaque fichier
              const filesData = await Promise.all(
                request.fileIds.map((fileId: string) => getFileDetails(fileId)),
              );

              const validFiles = filesData
                .filter((response: any) => response.status === 200)
                .map((response: any) => response.body as GetFileDetailsOutput);

              setFiles(validFiles);
              console.log("Fichiers récupérés:", validFiles);
            } catch (error) {
              console.error(
                "Erreur lors de la récupération des fichiers:",
                error,
              );
            } finally {
              setFilesLoading(false);
            }
          } else {
            setFiles([]);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des détails:", error);
          setDetailedRequest(request);
          setFiles([]);
        } finally {
          setLoading(false);
        }
      };

      fetchDetailedRequest();
    }
  }, [open, request?._id, request]);

  const currentRequest = detailedRequest || request;
  const statusInfo = getStatusInfo(currentRequest.status);

  const handleFileDownload = async (fileId: string, filename: string) => {
    try {
      const blob = await downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement du fichier:", error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        zIndex: 9999,
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header moderne */}
      <div className="bg-[#E3B500] text-white">
        <div className="p-4 sm:p-6 lg:p-8 flex justify-between items-center">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">
                Détails de la demande
              </h2>
              <p className="text-sm sm:text-base text-white/90 mt-1">
                Explication mathématique
              </p>
            </div>
          </div>
          <IconButton
            onClick={onClose}
            className="text-white hover:bg-white/20 transition-colors rounded-xl"
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
            }}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </IconButton>
        </div>
      </div>

      {/* Contenu moderne */}
      <div className="bg-gray-50 min-h-[400px] max-h-[70vh] overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E3B500]/20"></div>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E3B500] border-t-transparent absolute top-0 left-0"></div>
              </div>
              <span className="mt-4 text-gray-600 font-medium">
                Chargement des détails...
              </span>
            </div>
          ) : (
            /* Affichage des données simplifié et clair */
            <div className="space-y-6">
              {/* Informations principales */}
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {/* Statut */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-[#E3B500]" />
                      <span className="text-sm font-medium text-gray-700">
                        Statut
                      </span>
                    </div>
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.colorClass}`}
                    >
                      {statusInfo.icon}
                      {statusInfo.label}
                    </span>
                  </div>

                  {/* Classe */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">
                        Classe
                      </span>
                    </div>
                    <p className="text-base font-semibold text-gray-900">
                      {getSchoolClassLabel(
                        currentRequest.schoolClass ||
                          currentRequest.studentClass,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Informations de l'étudiant */}
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-[#E3B500]" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Informations de l'étudiant
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 font-medium mb-1">
                      Email
                    </p>
                    <p className="text-sm text-gray-800">
                      {currentRequest.studentEmail || "Non disponible"}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 font-medium mb-1">
                      Nom
                    </p>
                    <p className="text-sm text-gray-800">
                      {currentRequest.student?.firstname &&
                      currentRequest.student?.lastname
                        ? `${currentRequest.student.firstname} ${currentRequest.student.lastname}`
                        : "Non disponible"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description du problème */}
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-[#E3B500]" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Problème à résoudre
                  </h3>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {currentRequest.comment ||
                      currentRequest.description ||
                      "Aucune description du problème"}
                  </p>
                </div>
              </div>

              {/* Fichiers joints */}
              {currentRequest.fileIds && currentRequest.fileIds.length > 0 && (
                <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-[#E3B500]" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Fichiers joints
                    </h3>
                    <span className="bg-[#E3B500] text-white px-2 py-1 rounded-full text-xs font-medium">
                      {currentRequest.fileIds.length}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {filesLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#E3B500] border-t-transparent"></div>
                        <span className="ml-2 text-sm text-gray-600">
                          Chargement...
                        </span>
                      </div>
                    ) : files && files.length > 0 ? (
                      files.map((file: GetFileDetailsOutput) => (
                        <div
                          key={file._id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p
                                className="text-sm font-medium text-gray-900 truncate"
                                title={file.originalName}
                              >
                                {file.originalName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {file.mimeType} •{" "}
                                {formatFileSize(file.size || 0)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleFileDownload(file._id, file.originalName)
                            }
                            className="p-2 text-[#E3B500] hover:bg-[#E3B500]/10 rounded-lg transition-colors"
                            title="Télécharger"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Aucun fichier disponible
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Professeur assigné */}
              {(currentRequest.status === "validated" ||
                currentRequest.status === "slots_proposed" ||
                currentRequest.status === "student_confirmed" ||
                currentRequest.status === "meeting_scheduled" ||
                currentRequest.status === "scheduled" ||
                currentRequest.status === "completed") && (
                <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-[#E3B500]" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Professeur assigné
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        Nom
                      </p>
                      <p className="text-sm text-gray-800">
                        {currentRequest.teacher?.firstname &&
                        currentRequest.teacher?.lastname
                          ? `${currentRequest.teacher.firstname} ${currentRequest.teacher.lastname}`
                          : "Non disponible"}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        Email
                      </p>
                      <p className="text-sm text-gray-800">
                        {currentRequest.teacher?.email || "Non disponible"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Lien Meet */}
              {currentRequest.googleMeetLink && (
                <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Video className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      Lien de la séance
                    </h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-blue-900 mb-1">
                          Lien Google Meet
                        </p>
                        <p className="text-sm text-blue-800 truncate">
                          {currentRequest.googleMeetLink}
                        </p>
                      </div>
                      <a
                        href={currentRequest.googleMeetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Ouvrir le lien Meet"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer moderne */}
      <div className="bg-gray-100 border-t border-gray-200 p-4 sm:p-6">
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="group px-6 py-3 bg-[#E3B500] text-white rounded-xl flex items-center gap-2 hover:bg-[#CA8A04] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium text-sm sm:text-base"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-90 transition-transform duration-200" />
            <span>Fermer</span>
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ExplanationDetailsModal;
